import { billingApi, isFlowSwitchedOff } from "./billingApi";
import { BillingCode, BillingReason, SubscriptionStatus } from "./billingCodes";
import {
   formatBillingDate,
   formatBillingDateTime,
   isBillingDateAhead,
   parseBillingDate
} from "./billingDates";
import { isBillingOnly, useBillingStore } from "./billingStore";
import { isPlanDowngradesEnabled } from "./planDowngrades";

// What the billing page offers an organization held at billing_only, decided by
// its billing status, and what it does with what that fix reports back. Also
// when a refused change on the subscription page hands over to it.

export const BillingAction = Object.freeze({
   /** A payment is waiting on the bank's check (3-D Secure). */
   CONFIRM_PAYMENT: "confirmPayment",
   /** No subscription: pick a plan, on a trial if the organization has one left. */
   START_SUBSCRIPTION: "startSubscription",
   /** The subscription has ended: pick a plan again, of either kind of account. */
   RESUBSCRIBE: "resubscribe",
   /**
    * Billing details and a card, which resume or settle the subscription, or
    * start the one a plan was picked for with no trial left.
    */
   ADD_CARD: "addCard",
   /**
    * An annual plan ended unpaid and still owes its months up to the contract
    * end: pay that balance (the card on file, or a new one). No plans until it
    * is paid; paying it starts nothing, and the plans come after.
    */
   SETTLE_DEBT: "settleDebt",
   /**
    * Enterprise was picked and its terms aren't taken up: the offer to
    * subscribe to when our team has made it, our team's word otherwise. The
    * standard plans either way.
    */
   ENTERPRISE_OFFER: "enterpriseOffer",
   /** Nothing the page can do; the organization is pointed at a person. */
   CONTACT_US: "contactUs"
});

const CARD_RECOVERY_STATUSES = new Set([
   SubscriptionStatus.PAST_DUE,
   SubscriptionStatus.UNPAID,
   SubscriptionStatus.PAUSED,
   SubscriptionStatus.INCOMPLETE
]);

const ENDED_STATUSES = new Set([SubscriptionStatus.CANCELED, SubscriptionStatus.INCOMPLETE_EXPIRED]);

/**
 * The fix a status asks for. The reason decides; a status that arrives without
 * one — a 402 raised it before the page could read the rest, or the server named
 * a reason this app does not know yet — is read off what else it says.
 */
export const resolveBillingAction = (status) => {
   if (status?.requiresAction || status?.reason === BillingReason.PAYMENT_ACTION_REQUIRED) {
      return BillingAction.CONFIRM_PAYMENT;
   }

   switch (status?.reason) {
      case BillingReason.OUTSTANDING_DEBT:
         return BillingAction.SETTLE_DEBT;
      case BillingReason.NO_SUBSCRIPTION:
         return BillingAction.START_SUBSCRIPTION;
      case BillingReason.SUBSCRIPTION_CANCELED:
         return BillingAction.RESUBSCRIBE;
      // Its subscriptionStatus is "none" too, and no card is asked for yet.
      case BillingReason.ENTERPRISE_OFFER_PENDING:
         return BillingAction.ENTERPRISE_OFFER;
      // PAYMENT_METHOD_REQUIRED's subscriptionStatus is "none", which on its own
      // would read as a plan still to pick: the plan is picked, and only a card
      // is missing.
      case BillingReason.TRIAL_ENDED_NO_PAYMENT_METHOD:
      case BillingReason.PAYMENT_FAILED:
      case BillingReason.PAYMENT_METHOD_REQUIRED:
         return BillingAction.ADD_CARD;
      default:
         break;
   }

   if (status?.subscriptionStatus === SubscriptionStatus.NONE) return BillingAction.START_SUBSCRIPTION;
   if (ENDED_STATUSES.has(status?.subscriptionStatus)) return BillingAction.RESUBSCRIBE;
   if (!status?.hasPaymentMethod || CARD_RECOVERY_STATUSES.has(status?.subscriptionStatus)) {
      return BillingAction.ADD_CARD;
   }

   return BillingAction.CONTACT_US;
};

/**
 * Whether picking a plan now starts a free trial. An organization gets one,
 * ever: after it, every subscription is paid from the start, however the last
 * one ended. The server says which (`trialAvailable`). A status that doesn't
 * carry it — one a 402 raised before the page could read the rest — is taken
 * to have a trial only when it has never had a subscription.
 */
export const offersTrial = (status) =>
   typeof status?.trialAvailable === "boolean"
      ? status.trialAvailable
      : resolveBillingAction(status) === BillingAction.START_SUBSCRIPTION;

/**
 * True when a status holds the organization until a plan is picked: it has no
 * subscription — none was ever started, or Stripe no longer has the one there
 * was — or the one it had has ended, or it waits on Enterprise terms. A card
 * on file changes none of that. A card is not a subscription, and the plans
 * come first.
 */
export const isHeldForPlan = (status) => {
   if (!isBillingOnly(status)) return false;

   const action = resolveBillingAction(status);
   return (
      action === BillingAction.START_SUBSCRIPTION ||
      action === BillingAction.RESUBSCRIBE ||
      action === BillingAction.ENTERPRISE_OFFER
   );
};

/** What the billing page does with what a fix reports back. */
export const FixOutcome = Object.freeze({
   /** The organization is let back in: say so. */
   LET_IN: "letIn",
   /** The fix went through and the status has not caught up yet: wait for it. */
   WAIT: "wait",
   /** Something else is asked of the cardholder now, which the page shows. */
   SHOW_NEXT: "showNext",
   /** The bank turned the payment down, and the organization is still held. */
   DECLINED: "declined",
   /** The bank turned the payment down, but a grace period keeps the organization in. */
   DECLINED_STILL_IN: "declinedStillIn"
});

/**
 * How a fix came out. `action` is the fix that was on screen when it started,
 * `next` the status it left the organization in, and `error` the bank's
 * refusal of the payment, when there was one (paymentAction.js).
 *
 * A refusal is never waited on, even when the status asks for the same fix as
 * before: nothing is on its way from Stripe, and waiting would only hide why
 * the payment failed.
 */
export const resolveFixOutcome = (action, next, { error } = {}) => {
   if (!next) return FixOutcome.SHOW_NEXT;
   if (error) return isBillingOnly(next) ? FixOutcome.DECLINED : FixOutcome.DECLINED_STILL_IN;
   if (!isBillingOnly(next)) return FixOutcome.LET_IN;
   if (!next.requiresAction && resolveBillingAction(next) === action) return FixOutcome.WAIT;
   return FixOutcome.SHOW_NEXT;
};

/**
 * What a "Card saved" notice goes on to say, by the status the save left. With
 * no subscription for the card to pay — Stripe no longer has the one there was,
 * and /complete saved the card all the same — a plan is what comes next, and
 * there is no subscription to name as the one it is charged to.
 */
export const describeSavedCard = (status) =>
   isHeldForPlan(status)
      ? "Now pick a plan to start your subscription."
      : "It's now the card your subscription is charged to.";

/**
 * The banner the app's pages show while a payment is owed and the grace period
 * still lets the organization in — `graceEndsAt`, which the backend reports
 * only then — or null. Whoever may manage billing is told what to do before it
 * runs out, and offered the Subscription page (`action` names the button);
 * anybody else is told who can. `now`, `locale` and `timeZone` are there so a
 * test can pin them.
 */
export const describePaymentGrace = (status, canManageBilling, { now, locale, timeZone } = {}) => {
   if (!status || isBillingOnly(status) || !isBillingDateAhead(status.graceEndsAt, now)) {
      return null;
   }

   const until = formatBillingDateTime(status.graceEndsAt, { locale, timeZone });

   if (!canManageBilling) {
      return {
         title: "Your organization's last payment didn't go through",
         text: `Ask your organization's owner to settle it before ${until} to keep full access.`,
         action: null
      };
   }

   return status.requiresAction
      ? {
           title: "Your bank needs you to confirm a payment",
           text: `Confirm it before ${until} to keep full access.`,
           action: "Confirm payment"
        }
      : {
           title: "Your last payment didn't go through",
           text: `Update your card before ${until} to keep full access.`,
           action: "Update card"
        };
};

/** Said as a change refused for want of a subscription hands over to the plans. */
export const NO_SUBSCRIPTION_LEFT_MESSAGE =
   "Your organization no longer has a subscription, so nothing was changed. Pick a plan to start a new one.";

/**
 * After change-plan or undo-cancel refused: whether it was for want of a
 * subscription, with the billing page's plans taking over.
 *
 * Stripe can lose a subscription the backend still holds the id of — a deleted
 * test clock, "Delete all test data". Both calls then answer 409
 * NO_SUBSCRIPTION. Read again, billing-status forgets the subscription and
 * holds the organization for a plan, and the routes act on that at once: the
 * billing page and its plans replace the subscription page. So it is not an
 * error to report, only a move to explain.
 *
 * Resolves true once the organization is held for a plan. False for any other
 * refusal, a status that can't be read, or one that says otherwise, which the
 * caller reports as it always did.
 */
export async function recoverFromNoSubscription(failure, organizationId) {
   if (failure?.code !== BillingCode.NO_SUBSCRIPTION) return false;

   try {
      return isHeldForPlan(await billingApi.getBillingStatus(organizationId));
   } catch {
      return false;
   }
}

/**
 * Whether cancelling now lets the subscription run to the end of what it has
 * (a trial, or a period paid for) instead of ending at once. The backend ends
 * anything else immediately: nothing was paid for the current period.
 */
export const cancelsAtPeriodEnd = (status) =>
   status?.subscriptionStatus === SubscriptionStatus.TRIALING ||
   status?.subscriptionStatus === SubscriptionStatus.ACTIVE;

// An annual plan is billed monthly on a 12-month commitment. Its status says
// when the commitment ends (`commitmentEnd`, null on a monthly plan and during a
// trial), and, once a cancellation is scheduled, when it ends the subscription
// (`cancelAt`). `currentPeriodEnd` is only the end of the month paid for.

// The backend compares a commitment's end with a period's with an hour's slack
// (AnnualCommitment.cs): both come from the same anchor and can differ by the
// odd second.
const COMMITMENT_SLACK_MS = 60 * 60 * 1000;

const isAfterWithSlack = (later, earlier) => {
   const end = parseBillingDate(later);
   if (!end) return false;

   const start = parseBillingDate(earlier);
   return !start || end.valueOf() > start.valueOf() + COMMITMENT_SLACK_MS;
};

/**
 * Whether an active annual plan has more of its commitment left than the month
 * paid for. Cancelling it then lets it run, billed monthly, to `commitmentEnd`,
 * and change-plan refuses a monthly plan (COMMITMENT_ACTIVE). In its last
 * month, during a trial and on a monthly plan, neither holds. The backend's
 * test, from BillingService's cancel and change-plan.
 */
export const isCommittedPastThisPeriod = (status) =>
   status?.subscriptionStatus === SubscriptionStatus.ACTIVE &&
   outlastsPeriod(status?.commitmentEnd, status?.currentPeriodEnd);

/** Whether a commitment's end is after the period's, by more than the backend's slack. */
export const outlastsPeriod = (commitmentEnd, currentPeriodEnd) =>
   isAfterWithSlack(commitmentEnd, currentPeriodEnd);

/**
 * The old flow's subscription read (getStripeSubscriptionByOrganization) in the
 * shape the commitment tests read. Its `subscriptionStatus` is Stripe's raw
 * status too, and the current period ends at `subscriptionEndsAt`.
 */
export const oldFlowCommitment = (subscription) => ({
   subscriptionStatus: subscription?.subscriptionStatus ?? null,
   currentPeriodEnd: subscription?.subscriptionEndsAt ?? null,
   commitmentEnd: subscription?.commitmentEnd ?? null
});

/** Whether a cancellation is scheduled. Either field says so; the backend sets both. */
export const isCancellationScheduled = (status) =>
   Boolean(status?.cancelAtPeriodEnd || status?.cancelAt);

/**
 * When a scheduled cancellation ends the subscription: `cancelAt`; failing that,
 * an annual plan's `commitmentEnd`, since cancelling one stops it at the end of
 * its commitment, not of the month; and the end of the period only when there is
 * neither. Null when nothing is scheduled, so a cancellation taken back
 * (undo-cancel answers both cleared) shows no end.
 */
export const scheduledEndOf = (status) =>
   isCancellationScheduled(status)
      ? status.cancelAt ?? status.commitmentEnd ?? status.currentPeriodEnd ?? null
      : null;

/** Whether a scheduled end is after the month paid for: an annual plan still billed monthly until then. */
export const isBilledUntilScheduledEnd = (status) =>
   isCancellationScheduled(status) && isAfterWithSlack(scheduledEndOf(status), status.currentPeriodEnd);

/**
 * What an annual plan's commitment allows, with its end date when the status
 * has one. Under the plan-change rules (planChanges.js) a cheaper annual plan
 * waits for the end too, and a monthly one chosen in the last month starts
 * then, so only a bigger annual plan is "now".
 */
export const describeCommitment = (commitmentEnd) => {
   const date = formatBillingDate(commitmentEnd);
   const commitment = `Your annual plan is a 12-month commitment${date ? ` until ${date}` : ""}.`;
   return isPlanDowngradesEnabled()
      ? `${commitment} You can move to a bigger annual plan now. A smaller plan, or a monthly one chosen in its last month, starts when the commitment ends.`
      : `${commitment} You can move to another annual plan now, or to a monthly plan in its last month.`;
};

/**
 * Whether the billing page offers to cancel: only for a monthly plan left unpaid
 * (a renewal failed and its grace period is over). Nothing more is ever charged
 * for a paused one, an incomplete one or a plan still waiting for a card, so
 * there is nothing to stop.
 *
 * Not for an annual plan: cancelling an unpaid subscription ends it at once and
 * voids what it owes, and an annual plan's months stay due until its commitment
 * ends, however it is cancelled. That one pays, or talks to support
 * (cancelGoesThroughSupport).
 */
export const offersCancelOnBillingPage = (status) =>
   status?.subscriptionStatus === SubscriptionStatus.UNPAID && status?.isMonthly !== false;

/** Whether an unpaid subscription can only be cancelled through support: an annual plan's. */
export const cancelGoesThroughSupport = (status) =>
   status?.subscriptionStatus === SubscriptionStatus.UNPAID && status?.isMonthly === false;

/** How a cancel came out. */
export const CancelOutcome = Object.freeze({
   /** Cancelled (or already cancelled); the status it answered with is kept, and the server's message. */
   CANCELED: "canceled",
   /** Nothing to cancel: billing-status has been read again. */
   NO_SUBSCRIPTION: "noSubscription",
   /** The new flow is off for the organization: the old cancel applies. */
   FLOW_OFF: "flowOff"
});

/**
 * Cancel the subscription on the new flow (POST /Payment/cancel), whose status
 * replaces the one held (billingApi.js). A bare 404 is the flag switched off:
 * the organization is let go of, for the old flow's screens and its cancel.
 * Any other refusal (BILLING_BUSY once retried, STRIPE_ERROR) is thrown for the
 * caller to say.
 *
 * @returns {Promise<{outcome: string, status?: Object, message?: string|null}>}
 */
export async function cancelThroughBilling({
   organizationId,
   reasonOfCancellation,
   customReasonOfCancellation
}) {
   try {
      const { status, message } = await billingApi.cancel({
         organizationId,
         reasonOfCancellation,
         customReasonOfCancellation
      });
      return { outcome: CancelOutcome.CANCELED, status, message };
   } catch (failure) {
      if (isFlowSwitchedOff(failure)) {
         useBillingStore.getState().forget(organizationId);
         return { outcome: CancelOutcome.FLOW_OFF };
      }
      if (await recoverFromNoSubscription(failure, organizationId)) {
         return { outcome: CancelOutcome.NO_SUBSCRIPTION };
      }
      throw failure;
   }
}
